import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import config from '../config';
import { useAuth } from '../auth/AuthContext';
import {
  ResizableColGroup,
  ResizableHeaderCell,
  TableEmptyRow,
  TableLoadingRow,
  useResizableColumns,
} from '../components/table/ResizableTable';
import DetailModalShell from '../components/detail/DetailModalShell';
import JsonTreeViewer from '../components/detail/JsonTreeViewer';

const debounceDelay = 300;
const defaultRowsPerPage = 25;

const TABLE_COLUMNS = [
  { key: 'order', label: 'Order', minWidth: 220, maxWidth: 520, defaultWidth: 300 },
  { key: 'status', label: 'Status', minWidth: 120, maxWidth: 240, defaultWidth: 140 },
  { key: 'shop', label: 'Store', minWidth: 180, maxWidth: 380, defaultWidth: 240 },
  { key: 'recipient', label: 'Recipient', minWidth: 220, maxWidth: 480, defaultWidth: 300 },
  { key: 'items', label: 'Items', minWidth: 100, maxWidth: 180, defaultWidth: 110 },
  { key: 'total', label: 'Total', minWidth: 110, maxWidth: 220, defaultWidth: 130 },
  { key: 'shipping', label: 'Shipping', minWidth: 110, maxWidth: 220, defaultWidth: 130 },
  { key: 'updated', label: 'Updated', minWidth: 170, maxWidth: 340, defaultWidth: 210 },
];

const statusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_production', label: 'In production' },
  { value: 'fulfilled', label: 'Fulfilled' },
  { value: 'canceled', label: 'Canceled' },
];

function formatMoney(cents) {
  if (typeof cents !== 'number' || Number.isNaN(cents)) {
    return '-';
  }
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(value) {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return date.toLocaleString();
}

function labelize(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '-';
  }
  return raw
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function statusColor(status) {
  switch (String(status || '').toLowerCase()) {
    case 'fulfilled':
      return 'success';
    case 'canceled':
    case 'cancelled':
      return 'default';
    case 'in_production':
      return 'primary';
    case 'on_hold':
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
}

function addressObject(order) {
  return order?.address && typeof order.address === 'object' ? order.address : {};
}

function recipientName(order) {
  const address = addressObject(order);
  const name = [address.first_name, address.last_name].filter(Boolean).join(' ').trim();
  return name || address.company || '-';
}

function recipientLocation(order) {
  const address = addressObject(order);
  return [address.city, address.region, address.country].filter(Boolean).join(', ') || '-';
}

function fullAddress(order) {
  const address = addressObject(order);
  const lines = [
    [address.first_name, address.last_name].filter(Boolean).join(' '),
    address.company,
    address.address1,
    address.address2,
    [address.city, address.region, address.zip].filter(Boolean).join(', '),
    address.country,
    address.email,
    address.phone,
  ].filter(Boolean);
  return lines.length ? lines : ['-'];
}

function canSendToProduction(order) {
  return String(order?.status || '').toLowerCase() === 'on_hold';
}

function canCancel(order) {
  const status = String(order?.status || '').toLowerCase();
  return Boolean(status) && !['fulfilled', 'canceled', 'cancelled'].includes(status);
}

const PrintifyOrdersPage = () => {
  const { fetchWithAuth } = useAuth();
  const [shops, setShops] = React.useState([]);
  const [shopsLoading, setShopsLoading] = React.useState(false);
  const [selectedShopId, setSelectedShopId] = React.useState('');
  const [selectedStatus, setSelectedStatus] = React.useState('');
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [actionMessage, setActionMessage] = React.useState(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailOrder, setDetailOrder] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const { getWidth, startColumnResize, tableMinWidth } = useResizableColumns(TABLE_COLUMNS, {
    storageKey: 'table.printify-orders',
  });

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), debounceDelay);
    return () => window.clearTimeout(handle);
  }, [search]);

  React.useEffect(() => {
    setPage(0);
  }, [debouncedSearch, rowsPerPage, selectedShopId, selectedStatus]);

  const loadShops = React.useCallback(async () => {
    setShopsLoading(true);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/shops`);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to load Printify stores');
      }
      const payload = await response.json();
      const nextShops = (Array.isArray(payload.items) ? payload.items : []).filter((shop) => !shop.is_template);
      setShops(nextShops);
      setSelectedShopId((current) => (
        current && nextShops.some((shop) => shop.id === current) ? current : nextShops[0]?.id || ''
      ));
    } catch (err) {
      setError(err.message || 'Failed to load Printify stores');
    } finally {
      setShopsLoading(false);
    }
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadShops();
  }, [loadShops]);

  const loadOrders = React.useCallback(async () => {
    if (!selectedShopId) {
      setItems([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('shop_id', selectedShopId);
      params.set('limit', rowsPerPage.toString());
      params.set('offset', (page * rowsPerPage).toString());
      if (debouncedSearch) {
        params.set('q', debouncedSearch);
      }
      if (selectedStatus) {
        params.set('status', selectedStatus);
      }
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/orders?${params.toString()}`);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to load Printify orders');
      }
      const payload = await response.json();
      setItems(Array.isArray(payload.items) ? payload.items : []);
      setTotal(typeof payload.total === 'number' ? payload.total : 0);
    } catch (err) {
      setError(err.message || 'Failed to load Printify orders');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, fetchWithAuth, page, rowsPerPage, selectedShopId, selectedStatus]);

  React.useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleSync = async () => {
    if (!selectedShopId) {
      return;
    }
    setSyncing(true);
    setError(null);
    setActionMessage(null);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/orders/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop_id: selectedShopId }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to queue Printify order sync');
      }
      setActionMessage('Printify order sync queued.');
    } catch (err) {
      setError(err.message || 'Failed to queue Printify order sync');
    } finally {
      setSyncing(false);
    }
  };

  const openDetail = async (order) => {
    if (!order?.id) {
      return;
    }
    setDetailOpen(true);
    setDetailOrder(order);
    setDetailLoading(true);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/orders/${order.id}`);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to load Printify order');
      }
      const payload = await response.json();
      if (payload.item) {
        setDetailOrder(payload.item);
      }
    } catch (err) {
      setError(err.message || 'Failed to load Printify order');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOrderUpdated = React.useCallback((updatedOrder) => {
    if (!updatedOrder?.id) {
      return;
    }
    setDetailOrder(updatedOrder);
    setItems((previous) => previous.map((item) => (
      item.id === updatedOrder.id ? { ...item, ...updatedOrder } : item
    )));
  }, []);

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          select
          label="Printify Store"
          value={selectedShopId}
          onChange={(event) => setSelectedShopId(event.target.value)}
          sx={{ minWidth: 260 }}
          disabled={shopsLoading || shops.length === 0}
        >
          {shops.length === 0 ? (
            <MenuItem value="" disabled>
              {shopsLoading ? 'Loading...' : 'No stores'}
            </MenuItem>
          ) : (
            shops.map((shop) => (
              <MenuItem key={shop.id} value={shop.id}>
                {shop.title || shop.external_id}
              </MenuItem>
            ))
          )}
        </TextField>
        <TextField
          fullWidth
          placeholder="Search orders"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
        <TextField
          select
          label="Status"
          value={selectedStatus}
          onChange={(event) => setSelectedStatus(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="contained"
          startIcon={syncing ? <CircularProgress color="inherit" size={16} /> : <SyncIcon />}
          onClick={handleSync}
          disabled={!selectedShopId || syncing}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {syncing ? 'Syncing...' : 'Sync Orders'}
        </Button>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {actionMessage ? <Alert severity="success">{actionMessage}</Alert> : null}

      <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }} stickyHeader>
            <ResizableColGroup columns={TABLE_COLUMNS} getWidth={getWidth} />
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((column) => (
                  <ResizableHeaderCell
                    key={column.key}
                    column={column}
                    getWidth={getWidth}
                    onResizeStart={startColumnResize}
                  />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingRow colSpan={TABLE_COLUMNS.length} label="Loading Printify orders..." />
              ) : items.length === 0 ? (
                <TableEmptyRow colSpan={TABLE_COLUMNS.length} label="No Printify orders found." />
              ) : (
                items.map((order) => (
                  <TableRow
                    key={order.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openDetail(order)}
                  >
                    <TableCell>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap title={order.label || order.external_id}>
                          {order.label || order.external_id || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {order.external_id || order.id}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={labelize(order.status)} size="small" color={statusColor(order.status)} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap title={order.shop?.title}>
                        {order.shop?.title || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap title={recipientName(order)}>
                          {recipientName(order)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap title={recipientLocation(order)}>
                          {recipientLocation(order)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{order.quantity_total || order.line_item_count || 0}</TableCell>
                    <TableCell>{formatMoney(order.total_price)}</TableCell>
                    <TableCell>{formatMoney(order.total_shipping)}</TableCell>
                    <TableCell>{formatDate(order.updated_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(event, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <PrintifyOrderDetailDialog
        open={detailOpen}
        order={detailOrder}
        loading={detailLoading}
        onClose={() => setDetailOpen(false)}
        fetchWithAuth={fetchWithAuth}
        onOrderUpdated={handleOrderUpdated}
        onActionMessage={setActionMessage}
        onError={setError}
      />
    </Box>
  );
};

const PrintifyOrderDetailDialog = ({
  open,
  order,
  loading,
  onClose,
  fetchWithAuth,
  onOrderUpdated,
  onActionMessage,
  onError,
}) => {
  const [tab, setTab] = React.useState('summary');
  const [actionLoading, setActionLoading] = React.useState('');

  React.useEffect(() => {
    setTab('summary');
    setActionLoading('');
  }, [order?.id]);

  const runOrderAction = React.useCallback(async (action) => {
    if (!order?.id) {
      return;
    }
    setActionLoading(action);
    onError?.(null);
    onActionMessage?.(null);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/orders/${order.id}/${action}`, {
        method: 'POST',
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Printify order action failed');
      }
      if (payload.item) {
        onOrderUpdated?.(payload.item);
      }
      onActionMessage?.(action === 'cancel' ? 'Order canceled.' : 'Order sent to production.');
    } catch (err) {
      onError?.(err.message || 'Printify order action failed');
    } finally {
      setActionLoading('');
    }
  }, [fetchWithAuth, onActionMessage, onError, onOrderUpdated, order?.id]);

  const lineItems = Array.isArray(order?.line_items) ? order.line_items : [];
  const shipments = Array.isArray(order?.shipments) ? order.shipments : [];

  const left = loading ? (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
      <CircularProgress size={24} />
    </Box>
  ) : order ? (
    <Stack spacing={2}>
      <Tabs value={tab} onChange={(event, next) => setTab(next)} aria-label="Printify order details">
        <Tab value="summary" label="Summary" />
        <Tab value="items" label="Line Items" />
        <Tab value="shipments" label="Shipments" />
        <Tab value="data" label="Data" />
      </Tabs>
      {tab === 'summary' ? <OrderSummary order={order} /> : null}
      {tab === 'items' ? <OrderLineItems items={lineItems} /> : null}
      {tab === 'shipments' ? <OrderShipments shipments={shipments} /> : null}
      {tab === 'data' ? <JsonTreeViewer data={order.raw_data || order} /> : null}
    </Stack>
  ) : (
    <Typography color="text.secondary">No order selected.</Typography>
  );

  const right = order ? (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
        <Chip label={labelize(order.status)} color={statusColor(order.status)} />
        {order.is_printify_express ? <Chip label="Printify Express" variant="outlined" /> : null}
        {order.is_economy_shipping ? <Chip label="Economy Shipping" variant="outlined" /> : null}
      </Stack>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Store
        </Typography>
        <Typography variant="body2">{order.shop?.title || '-'}</Typography>
        <Typography variant="caption" color="text.secondary">
          {order.shop?.sales_channel || 'Printify'} - External ID: {order.shop?.external_id || '-'}
        </Typography>
      </Box>
      <Divider />
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Totals
        </Typography>
        <Stack spacing={0.75}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Items</Typography>
            <Typography variant="body2">{formatMoney(order.total_price)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Shipping</Typography>
            <Typography variant="body2">{formatMoney(order.total_shipping)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Tax</Typography>
            <Typography variant="body2">{formatMoney(order.total_tax)}</Typography>
          </Stack>
        </Stack>
      </Box>
      <Divider />
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Ship To
        </Typography>
        {fullAddress(order).map((line, index) => (
          <Typography key={`${line}-${index}`} variant="body2">
            {line}
          </Typography>
        ))}
      </Box>
      <Divider />
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Timeline
        </Typography>
        <Typography variant="body2">Created: {formatDate(order.created_at)}</Typography>
        <Typography variant="body2">Updated: {formatDate(order.updated_at)}</Typography>
      </Box>
    </Stack>
  ) : null;

  const actions = order ? (
    <>
      <Button onClick={onClose}>Close</Button>
      <Button
        color="error"
        variant="outlined"
        startIcon={actionLoading === 'cancel' ? <CircularProgress color="inherit" size={16} /> : <CancelIcon />}
        disabled={!canCancel(order) || Boolean(actionLoading)}
        onClick={() => runOrderAction('cancel')}
      >
        {actionLoading === 'cancel' ? 'Canceling...' : 'Cancel Order'}
      </Button>
      <Button
        variant="contained"
        startIcon={actionLoading === 'send-to-production' ? <CircularProgress color="inherit" size={16} /> : <LocalShippingIcon />}
        disabled={!canSendToProduction(order) || Boolean(actionLoading)}
        onClick={() => runOrderAction('send-to-production')}
      >
        {actionLoading === 'send-to-production' ? 'Sending...' : 'Send to Production'}
      </Button>
    </>
  ) : (
    <Button onClick={onClose}>Close</Button>
  );

  return (
    <DetailModalShell
      open={open}
      onClose={onClose}
      title={loading ? 'Loading order...' : order?.label || order?.external_id || 'Printify Order'}
      externalId={order?.external_id || order?.id}
      left={left}
      right={right}
      actions={actions}
    />
  );
};

const OrderSummary = ({ order }) => (
  <Stack spacing={2}>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <Metric label="Line items" value={order.line_item_count ?? (order.line_items || []).length} />
      <Metric label="Units" value={order.quantity_total ?? 0} />
      <Metric label="Shipping method" value={order.shipping_method ?? '-'} />
    </Stack>
    <Divider />
    <Stack spacing={1}>
      <Typography variant="subtitle2">Recipient</Typography>
      <Typography variant="body2">{recipientName(order)}</Typography>
      <Typography variant="body2" color="text.secondary">{recipientLocation(order)}</Typography>
    </Stack>
    <Divider />
    <Stack spacing={1}>
      <Typography variant="subtitle2">Notification</Typography>
      <Typography variant="body2">
        {order.send_shipping_notification ? 'Shipping notification enabled' : 'Shipping notification disabled'}
      </Typography>
    </Stack>
  </Stack>
);

const Metric = ({ label, value }) => (
  <Box sx={{ flex: 1, minWidth: 140 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6">{value}</Typography>
  </Box>
);

const OrderLineItems = ({ items }) => {
  if (items.length === 0) {
    return <Typography color="text.secondary">No line items recorded.</Typography>;
  }
  return (
    <Stack spacing={1.5}>
      {items.map((item, index) => (
        <Paper key={item.id || `${item.variant_id}-${index}`} variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap title={item.product?.title || item.sku}>
                  {item.product?.title || item.sku || `Line item ${index + 1}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Variant: {item.variant_id || '-'} - SKU: {item.sku || '-'}
                </Typography>
              </Box>
              <Chip label={labelize(item.status)} size="small" variant="outlined" />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Typography variant="body2">Quantity: {item.quantity || 0}</Typography>
              <Typography variant="body2">Cost: {formatMoney(item.cost)}</Typography>
              <Typography variant="body2">Shipping: {formatMoney(item.shipping_cost)}</Typography>
            </Stack>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

const OrderShipments = ({ shipments }) => {
  if (shipments.length === 0) {
    return <Typography color="text.secondary">No shipments recorded.</Typography>;
  }
  return (
    <Stack spacing={1.5}>
      {shipments.map((shipment, index) => (
        <Paper key={shipment.id || shipment.tracking_number || index} variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={0.75}>
            <Typography variant="subtitle2">{shipment.carrier || 'Shipment'}</Typography>
            <Typography variant="body2">Tracking: {shipment.tracking_number || '-'}</Typography>
            <Typography variant="body2">Delivered: {formatDate(shipment.delivered_at)}</Typography>
            {shipment.tracking_url ? (
              <Button href={shipment.tracking_url} target="_blank" rel="noopener noreferrer" size="small" sx={{ alignSelf: 'flex-start' }}>
                Open Tracking
              </Button>
            ) : null}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

export default PrintifyOrdersPage;
